'use client';

import { Button, VisuallyHidden } from '@zillow/constellation-10';
import { type ComponentProps, useId } from 'react';

export type GateReason = string | false | undefined;

type GatedButtonProps = ComponentProps<typeof Button> & {
  /**
   * Why the button is shut, or `undefined` while it is open. Also pass `undefined`
   * when the reason is one the label already carries — an in-flight submit, say.
   */
  gateReason: GateReason;
};

/**
 * A `Button` that tells assistive tech why it is shut.
 *
 * A plain `disabled` Button renders the native `disabled` attribute, which drops
 * it from the tab order and stops `aria-describedby` from reaching a screen
 * reader. So this always renders through Constellation's `asChild` path
 * instead: Constellation supplies `aria-disabled` plus capture-phase handlers
 * that block every interaction while `disabled` (or `loading`) is true (see
 * `useDisabledInteractions` in `@zillow/constellation-10`), and it never
 * applies the native `disabled` attribute to an `asChild` root. We only need
 * to override its `tabIndex={-1}` back to `0` on our own inner `<button>`,
 * since Radix's `Slot` merge keeps a child's own explicit prop over the
 * slot's for anything but handlers/style/className. `asChild` must stay
 * unconditionally `true` — flipping it based on `disabled` would swap the
 * rendered element type and remount the button (losing focus) every time the
 * gate opens or shuts.
 */
export function GatedButton({
  gateReason,
  disabled,
  children,
  'aria-describedby': callerDescribedBy,
  ...buttonProps
}: GatedButtonProps) {
  const hintId = useId();
  // The gate hint adds to whatever the caller already points at rather than replacing it.
  const describedBy = [callerDescribedBy, gateReason ? hintId : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <>
      <Button {...buttonProps} disabled={disabled} asChild aria-describedby={describedBy}>
        <button type={buttonProps.type ?? 'button'} tabIndex={0}>
          {children}
        </button>
      </Button>
      {gateReason ? <VisuallyHidden id={hintId}>{gateReason}</VisuallyHidden> : null}
    </>
  );
}
